// UTM Parameter Tracking Utility
// This utility captures and stores UTM parameters from the URL for later use when creating user accounts

/**
 * Extracts UTM parameters from the current URL
 * @returns {Object} Object containing UTM parameters
 */
export const extractUtmParams = () => {
  if (typeof window === 'undefined') return {};
  
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {};
  
  // List of UTM parameters we want to track
  const utmKeys = [
    'utm_source',
    'utm_medium', 
    'utm_campaign',
    'utm_term',
    'utm_content'
  ];
  
  utmKeys.forEach(key => {
    const value = urlParams.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });
  
  return utmParams;
};

/**
 * Stores UTM parameters in localStorage with a timestamp
 * @param {Object} utmParams - UTM parameters to store
 */
export const storeUtmParams = (utmParams) => {
  if (typeof window === 'undefined') return;
  
  if (Object.keys(utmParams).length > 0) {
    const utmData = {
      ...utmParams,
      timestamp: Date.now(),
      // Store the full URL for reference
      referrer_url: window.location.href
    };
    
    localStorage.setItem('utm_tracking_data', JSON.stringify(utmData));
    console.log('UTM parameters stored:', utmData);
  }
};

/**
 * Retrieves stored UTM parameters from localStorage
 * @returns {Object|null} Stored UTM data or null if not found/expired
 */
export const getStoredUtmParams = () => {
  if (typeof window === 'undefined') return null;
  
  try {
    const storedData = localStorage.getItem('utm_tracking_data');
    if (!storedData) return null;
    
    const utmData = JSON.parse(storedData);
    
    // Check if data is older than 30 days (30 * 24 * 60 * 60 * 1000 ms)
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
    if (Date.now() - utmData.timestamp > thirtyDaysInMs) {
      localStorage.removeItem('utm_tracking_data');
      return null;
    }
    
    return utmData;
  } catch (error) {
    console.error('Error retrieving UTM data:', error);
    return null;
  }
};

/**
 * Clears stored UTM parameters (useful after successful account creation)
 */
export const clearStoredUtmParams = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('utm_tracking_data');
};

/**
 * Initializes UTM tracking on page load
 * Should be called in _app.js or main layout component
 */
export const initializeUtmTracking = () => {
  if (typeof window === 'undefined') return;
  
  // Only capture UTM params if we don't already have them stored
  const existingUtmData = getStoredUtmParams();
  if (!existingUtmData) {
    const utmParams = extractUtmParams();
    storeUtmParams(utmParams);
  }
}; 